import SkeletonTable from "components/display/Skeleton/SkeletonTable";
import { Checkbox } from "components/forms/fragments/Checkbox";
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import TableRowActions from "./TableRowActions";
import { Column, TableAction, TableConfig } from "./types";

export interface TableBodyProps<T extends Record<string, any>> {
  columns: Column<T>[];
  data: T[];
  selectable: boolean;
  selectedRows: Set<number>;
  actions?: TableAction<T>[];
  styling?: TableConfig<T>['styling'];
  isLoading?: boolean;
  skeletonRowCount?: number;
  centerActions?: boolean;
  /** Expansion de ligne : contenu rendu sous la ligne dépliée (chargé à la demande). */
  expandable?: { render: (row: T) => React.ReactNode };
  onSelectRow: (index: number, checked: boolean) => void;
  onRowClick?: (row: T, index: number) => void;
  getRowClassName?: (row: T, index: number) => string;
}

const TableBody = <T extends Record<string, any>>({
  columns,
  data,
  selectable,
  selectedRows,
  actions,
  styling,
  isLoading = false,
  skeletonRowCount = 5,
  centerActions = false,
  expandable,
  onSelectRow,
  onRowClick,
  getRowClassName
}: TableBodyProps<T>) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const colSpan =
    columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0) + (expandable ? 1 : 0);
  // Si en loading, afficher le skeleton
  if (isLoading) {
    return (
      <SkeletonTable
        columns={columns}
        selectable={selectable}
        actions={!!actions}
        styling={styling}
        rowCount={skeletonRowCount}
      />
    );
  }

  // Si pas de données, afficher un état vide
  if (data.length === 0) {
    return (
      <tbody className="bg-white">
        <tr>
          <td 
            colSpan={columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)}
            className="px-6 py-12 text-center text-gray-500"
          >
            Aucune donnée disponible
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody className="bg-white divide-y divide-gray-200">
      {data.map((row, index) => {
        const isExpanded = expandable != null && expandedIndex === index;
        const handleRowClick = expandable
          ? () => setExpandedIndex((cur) => (cur === index ? null : index))
          : onRowClick
            ? () => onRowClick(row, index)
            : undefined;
        return (
        <React.Fragment key={index}>
        <tr
          onClick={handleRowClick}
          className={`${styling?.rowClass || ''} ${
            index % 2 === 0 ? styling?.evenRowClass || '' : ''
          } ${styling?.hoverClass || 'hover:bg-gray-50'} ${
            handleRowClick ? 'cursor-pointer' : ''
          } ${isExpanded ? 'bg-primary/5' : ''} ${getRowClassName?.(row, index) || ''} transition-colors`}
        >
          {selectable && (
            <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={selectedRows.has(index)}
                onChange={(checked) => onSelectRow(index, checked)}
              />
            </td>
          )}
          {expandable && (
            <td className="w-8 pl-4 text-primary">
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </td>
          )}
          {columns.map((column) => (
            <td
              key={column.key}
              className={`px-6 py-4 text-sm whitespace-nowrap text-${
                column.align || 'left'
              } ${column.className || ''}`}
            >
              {column.render
                ? column.render(row[column.key], row, index)
                : row[column.key] || '—'}
            </td>
          ))}
          {actions && (
            <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
              <TableRowActions
                actions={actions}
                row={row}
                index={index}
                align={centerActions ? 'center' : 'end'}
              />
            </td>
          )}
        </tr>
        {isExpanded && (
          <tr className="bg-gray-50/60">
            <td colSpan={colSpan} className="px-6 py-4">
              {expandable!.render(row)}
            </td>
          </tr>
        )}
        </React.Fragment>
        );
      })}
    </tbody>
  );
};

export default TableBody;
